import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { mediaUrl } from "@/lib/api";

const FALLBACK_VIDEO = "https://assets.mixkit.co/videos/preview/mixkit-fashion-model-walking-on-a-runway-41556-large.mp4";

export const HeroVideo = ({ hero }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const cacheKey = `hero_video_src_${isMobile ? "m" : "d"}`;
  const picked = isMobile ? hero?.mobile_video || hero?.desktop_video : hero?.desktop_video || hero?.mobile_video;
  const src = hero ? (picked ? mediaUrl(picked) : FALLBACK_VIDEO) : localStorage.getItem(cacheKey);

  useEffect(() => { if (hero && src) localStorage.setItem(cacheKey, src); }, [hero, src, cacheKey]);
  const autoplay = hero?.autoplay !== false;
  const loop = hero?.loop !== false;

  useEffect(() => {
    setReady(false);
    const v = videoRef.current;
    if (!v) return;
    if (muted) { v.muted = true; v.defaultMuted = true; v.setAttribute("muted", ""); }
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    if (autoplay) v.play().catch(() => {});
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps
  const reveal = () => setReady(true);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  return (
    <section data-testid="hero-video" className="relative h-[100svh] w-full overflow-hidden bg-black">
      {src && <video
        ref={videoRef}
        key={src}
        data-testid="hero-video-el"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ backgroundColor: "#000" }}
        src={src}
        preload="auto"
        onPlaying={reveal}
        onLoadedData={reveal}
        onTimeUpdate={ready ? undefined : (e) => { if (e.currentTarget.currentTime > 0) reveal(); }}
        onError={() => setReady(false)}
        autoPlay={autoplay}
        loop={loop}
        muted={muted}
        playsInline
      />}
      <div
        data-testid="hero-video-cover"
        aria-hidden="true"
        className={`absolute inset-0 bg-black pointer-events-none transition-opacity duration-700 ease-out ${ready ? "opacity-0" : "opacity-100"}`}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/70" />

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <p className="font-mono-label text-xs uppercase tracking-luxe text-white/70 mb-6 reveal">
          {hero?.subtitle || "The Winter Collection"}
        </p>
        <h1 className="font-serif-display text-5xl sm:text-7xl lg:text-8xl font-light uppercase tracking-[0.12em] text-white leading-none reveal" style={{ animationDelay: "120ms" }}>
          {hero?.title || "Ignota Pars"}
        </h1>
        <Link
          to="/collection"
          data-testid="hero-shop-now-btn"
          className="mt-10 inline-block border border-white text-white px-10 py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-white hover:text-black reveal"
          style={{ animationDelay: "240ms" }}
        >
          Explore the Drop
        </Link>
      </div>

      <button
        data-testid="hero-mute-toggle"
        onClick={toggleMute}
        className="absolute bottom-8 right-8 z-10 border border-white/30 text-white/80 p-3 hover:bg-white hover:text-black transition-colors"
        aria-label="Toggle sound"
      >
        {muted ? <VolumeX strokeWidth={1.3} size={18} /> : <Volume2 strokeWidth={1.3} size={18} />}
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/60 animate-bounce">
        <ChevronDown strokeWidth={1.3} />
      </div>
    </section>
  );
};
